import { createTheme } from '@mui/material/styles';

const colorVariables = {
	textPrimary: '#767676',

	primary: '#1976d2',
	primaryLight: '#42a5f5',
	primaryDark: '#1565c0',
	primaryContrastText: '#ffffff',

	secondary: '#dc004e',
	secondaryLight: '#ff80ab',
	secondaryDark: '#b71c1c',
	secondaryContrastText: '#ffffff',

	error: '#d32f2f',
	warning: '#fbc02d',
	info: '#4fc3f7',
	success: '#2e7d32',

	// Glass
	glass: {
		text: '#e0e0e0'
	},

	// Backgrounds
	backgroundDefault: '#fafafa',
	backgroundPaper: '#ffffff',
	backgroundSecondary: '#f8f9fa',

	// Additional theme colors
	footerBackground: '#212121',
	overlayColor: 'rgba(0, 0, 0, 0.03)',
	borderColor: 'rgba(0,0,0,0.20)',

	// Text Colors
	textSecondary: '#757575',
	textDisabled: '#b0b0b0'
};

const OutfitFont = '"Outfit", sans-serif';

const lightTheme = createTheme({
	palette: {
		mode: 'light',

		primary: {
			main: colorVariables.primary,
			light: colorVariables.primaryLight,
			dark: colorVariables.primaryDark,
			contrastText: colorVariables.primaryContrastText
		},

		secondary: {
			main: colorVariables.secondary,
			light: colorVariables.secondaryLight,
			dark: colorVariables.secondaryDark,
			contrastText: colorVariables.secondaryContrastText
		},

		glass: {
			main: colorVariables.glass.text
		},

		error: {
			main: colorVariables.error,
			contrastText: '#fff'
		},

		warning: {
			main: colorVariables.warning,
			contrastText: '#333'
		},

		info: {
			main: colorVariables.info,
			contrastText: '#333'
		},

		success: {
			main: colorVariables.success,
			contrastText: '#fff'
		},

		background: {
			default: colorVariables.backgroundDefault,
			paper: colorVariables.backgroundPaper,
			secondary: colorVariables.backgroundSecondary
		},

		text: {
			primary: colorVariables.textPrimary,
			secondary: colorVariables.textSecondary,
			disabled: colorVariables.textDisabled
		}
	},

	typography: {
		fontFamily: OutfitFont,
		fontWeightLight: 300,
		fontWeightRegular: 400,
		fontWeightMedium: 500,
		fontWeightBold: 700,

		h1: { fontSize: '3rem', letterSpacing: '.05em' },
		h2: { fontSize: '2.5rem' },
		h3: { fontSize: '2rem' },
		h4: { fontSize: '1.75rem' },
		h5: { fontSize: '1.5rem' },
		h6: { fontSize: '1.25rem' },

		body1: { fontSize: '1rem' },
		button: { textTransform: 'none' },
		caption: { fontSize: '0.75rem' }
	},

	shape: { borderRadius: 8 },

	shadows: [
		'none',
		'0px 2px 4px rgba(0,0,0,0.12)',
		'0px 3px 5px rgba(0,0,0,0.16)'
	],

	components: {
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundColor: 'rgba(255, 255, 255, 0.2)',
					backdropFilter: 'blur(16px)',
					borderColor: 'rgba(255, 255, 255, 0.4)',
					borderWidth: 1,
					borderRadius: 8,
					boxShadow: 'none'
				}
			}
		},
		// MuiFormLabel: {
		// 	styleOverrides: {
		// 		root: {
		// 			'&.Mui-focused': {
		// 				color: colorVariables.glass.text
		// 			}
		// 		}
		// 	}
		// },
		// MuiInputLabel: {
		// 	styleOverrides: {
		// 		root: {
		// 			color: colorVariables.glass.text
		// 		}
		// 	}
		// },
		// MuiInputBase: {
		// 	styleOverrides: {
		// 		input: {
		// 			'&::placeholder': {
		// 				color: colorVariables.glass.text
		// 			},
		// 			'&:hover fieldset': {
		// 				borderColor: colorVariables.glass.text
		// 			},
		// 			'&:-webkit-autofill': {
		// 				WebkitBoxShadow: '0 0 0 100px rgba(0,0,0,0.6) inset', // Your background color
		// 				WebkitTextFillColor: colorVariables.glass.text // Your text color
		// 			}
		// 		}
		// 	}
		// },

		// MuiTextField: {
		// 	defaultProps: {
		// 		variant: 'outlined'
		// 	},
		// 	styleOverrides: {
		// 		root: {
		// 			'& fieldset': {
		// 				borderColor: colorVariables.glass.text
		// 			},
		// 			'&:hover fieldset': {
		// 				borderColor: colorVariables.glass.text
		// 			},
		// 			'&.Mui-focused fieldset': {
		// 				borderColor: colorVariables.glass.text
		// 			}
		// 		}
		// 	}
		// },
		// MuiOutlinedInput: {
		// 	styleOverrides: {
		// 		root: {
		// 			'& fieldset': {
		// 				borderColor: colorVariables.glass.text
		// 			},
		// 			'&:hover fieldset': {
		// 				borderColor: colorVariables.glass.text
		// 			},
		// 			'&.Mui-focused fieldset': {
		// 				borderColor: colorVariables.glass.text
		// 			},
		// 			'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
		// 				borderColor: colorVariables.glass.text
		// 			},
		// 			'&:hover .MuiOutlinedInput-notchedOutline': {
		// 				borderColor: colorVariables.glass.text
		// 			}
		// 		}
		// 	}
		// },

		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					borderRadius: 8,
					boxShadow: 'none',
					fontWeight: 500
				},
				contained: { boxShadow: 'none' },
				outlined: {
					borderWidth: 1,
					boxShadow: 'none'
				}
			}
		},

		MuiCard: {
			styleOverrides: {
				root: { boxShadow: 'none' } // ✅ Card inherits from Paper
			}
		},

		MuiTableCell: {
			styleOverrides: {
				root: {
					boxShadow: 'none',
					'&.elevation-0': {}
				}
			}
		},

		MuiList: {
			styleOverrides: {
				root: {
					boxShadow: 'none'
				}
			}
		}
	}
});

export default lightTheme;
